import React from 'react';
import {
  LayoutPage,
  LayoutContent,
  ApprovedCreditLimit,
  SaveAndCompleteLaterButton,
  Errors,
  FileIcon,
  MobileIcon,
} from 'components';
import { Container, Button, Form, Text, Grid, Stack, Flex } from 'basis';
import { useHistory } from 'react-router-dom';
import { useStoreValue, PROCESSING_ACCEPTANCE } from 'store';
import { useTheme } from '@emotion/react';
import { submitHandler } from 'utils';
import { formatNumber } from 'utils/formatNumber';
import { PrimaryButtonWrapper } from 'components/form/FormButtons';
import { STEP_PROCESSING_ACCEPTANCE } from '_config';
import { useAcceptPcaContract, useDataLayerAndAnalyticsLinks, useErrorTrackingAndUpdateStore } from 'hooks';

export const CreditLimitAllocation = () => {
  const [storeState, updateStore] = useStoreValue();
  const theme = useTheme();
  const history = useHistory();
  const { trackEventSender } = useDataLayerAndAnalyticsLinks(storeState);
  const { handleErrorCallback } = useErrorTrackingAndUpdateStore('Next');

  const { creditLimitApproved } = storeState;

  const handleSubmitSuccess = () => {
    updateStore({
      applicationErrors: null,
      activeStep: STEP_PROCESSING_ACCEPTANCE,
      applicationStatus: PROCESSING_ACCEPTANCE,
    });

    history.push(STEP_PROCESSING_ACCEPTANCE);
  };

  const { submit, loading: submitting } = useAcceptPcaContract({
    applicationId: storeState.applicationId,
    onSuccess: handleSubmitSuccess,
    onError: handleErrorCallback,
  });

  const submitValues = () => {
    return {
      acceptedCreditLimit: storeState.creditLimitApproved,
      accepted: true,
    };
  };

  const onSubmit = ({ values, errors }) => {
    const handle = () => {
      const newAcquisition = {
        ...storeState.acquisition,
        acceptance: { ...values },
      };
      updateStore({ acquisition: newAcquisition }); 

      trackEventSender(
        {
          category: 'application',
          action: 'application-submitted',
          location: 'credit-limit-allocation',
          label: 'Accept credit card limit',
          value: storeState.creditLimitApproved,
        },
        { applicationStatus: PROCESSING_ACCEPTANCE },
      );

      submit(submitValues());
    };
    submitHandler({ submit: handle, errors });
  };

  if (storeState.applicationErrors) {
    return (
      <LayoutPage>
        <Errors applicationRef={storeState.applicationRef} />
      </LayoutPage>
    );
  }

  return (
    <LayoutPage hideBackButton>
      <Container margin="0 0 13 0" hasBreakpointWidth>
        <Form id="confirmMinimumCreditLimit" onSubmit={onSubmit} disableFormCache>
          {formData => {
            return (
              <LayoutContent rowsGap="0" noSessionTimeout>
                <Container margin="8 0 0 0">
                  <Text as="h1" textStyle="heading5" textStyle-md="heading4" color={theme?.headingColor}>
                    Credit limit allocation
                  </Text>
                  <Container margin="10 0 5 0">
                    <Text>We can conditionally offer you the credit limit of:</Text>
                  </Container>
                  <ApprovedCreditLimit creditLimitApproved={creditLimitApproved} />
                  <Container margin="8 0" />
                </Container>
                <Stack gap="4">
                  <Flex>
                    <Container margin="0 3 0 0">
                      <FileIcon />
                    </Container>
                    <Text>We may ask you to provide supporting information to assess your request.</Text>
                  </Flex>
                  <Flex>
                    <Container margin="0 3 0 0">
                      <MobileIcon />
                    </Container>
                    <Text>
                      Once your account is set up, you can manage your credit limit anytime in the Latitude App or
                      Latitude Service Centre.
                    </Text>
                  </Flex>
                </Stack>
                <Container margin="8 0 0 0">
                  <Grid rowsGap="4" colsGap="10" cols="2">
                    <Grid.Item colSpan="all" colSpan-sm="0" alignItems="center">
                      <PrimaryButtonWrapper>
                        <Grid>
                          <Button width="100%" type="submit" loading={submitting}>
                            Accept ${formatNumber(storeState.creditLimitApproved)} credit card limit
                          </Button>
                        </Grid>
                      </PrimaryButtonWrapper>
                    </Grid.Item>
                    <Grid.Item colSpan="all" rowSpan="1" colSpan-sm="1" rowSpan-sm="0">
                      <Grid>
                        <SaveAndCompleteLaterButton />
                      </Grid>
                    </Grid.Item>
                  </Grid>
                </Container>
              </LayoutContent>
            );
          }}
        </Form>
      </Container>
    </LayoutPage>
  );
};
